/// <amd-module name="Feed/Legacy/Protocols/Common/IPackageProtocol" />
import * as React from "react";

import { IVssIconProps } from "VSSUI/VssIcon";

import { FeedView, Package, PackageMetrics, PackageVersion, PackageVersionMetrics } from "Feed/Common/Contracts/Feed";
import { UpstreamingBehavior } from "Feed/Common/Protocols/Shared/Contracts/PackagingShared";
import { ISupportedFeatures } from "Feed/Legacy/Protocols/Common/ISupportedFeatures";
import { MinimalPackageDetails } from "Feed/Legacy/Protocols/Common/WebApi/PackagingShared.Contracts";
import { HubAction } from "Feed/Legacy/Types/IHubState";
import { IPackageCommand } from "Feed/Legacy/Types/IPackageCommand";
import { Feed } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";

export interface IPackageProtocol {
    name: string;
    key: string;
    vssIconProps: IVssIconProps;
    supportedCommandsMask: number;
    clients: IPackageProtocolClient[];

    getOverviewAttributesPanel(
        feed: Feed,
        packageSummary: Package,
        packageVersion: PackageVersion,
        packageVersionMetrics: PackageVersionMetrics,
        packageMetrics: PackageMetrics,
        isSmartDependenciesEnabled: boolean,
        isProvenanceEnabled: boolean,
        isMetricsEnabled: boolean,
        isMetricsLoading: boolean
    ): React.ReactNode;

    getOverviewContentPanel(
        feed: Feed,
        feedViews: FeedView[],
        packageSummary: Package,
        packageVersion: PackageVersion
    ): React.ReactNode;

    /**
     * Provide selectedPackages on package list
     * Provide selectedVersions on versions list
     */
    getMultiSelectPackageCommands(
        feed: Feed,
        selectedPackages?: Package[],
        selectedVersions?: PackageVersion[]
    ): IPackageCommand[];

    getPackageCommands(
        packageSummary: Package,
        packageVersion: PackageVersion,
        feed: Feed,
        viaPackageList: boolean
    ): IPackageCommand[];

    /**
     * @param packageSummary The selected package to get the message for
     * @param packageVersion The version of the package
     * Note: This method is being skipped for the recycle bin page. This is because
     * for recycle bin we should not show the deleted/deprecated message. If other
     * messages are added that should be on the recycle bin page make sure that the
     * deleted/deprecated messages don't show up.
     */
    getPackageMessage(packageSummary: Package, packageVersion: PackageVersion): string;

    /**
     * @param feed
     * Returns an object ISupportedFeatures that specifies the state of protocol specific features
     */
    supportedFeatures(feed?: Feed): ISupportedFeatures;

    // DO NOT USE
    // This must stay for now because connect to feed dialog
    // that is located in the old Feed/Service/Web location still uses it.
    // Must be removed once connect to feed dialog gets updated.
    supportsUpstreams(feed?: Feed): boolean;

    /// Delete Operations
    deleteLatestVersions(projectId: string, feed: Feed, pkgs: Package[]): Promise<void>;

    deletePackageVersions(projectId: string, feed: Feed, pkg: Package, versions: PackageVersion[]): Promise<void>;

    restorePackageVersionsToFeed(
        projectId: string,
        feed: Feed,
        pkg: Package,
        versions: PackageVersion[]
    ): Promise<void>;

    permanentlyDeletePackageVersions(
        projectId: string,
        feed: Feed,
        pkg: Package,
        versions: PackageVersion[]
    ): Promise<void>;
    permanentlyDeletePackageVersions(
        projectId: string,
        feed: Feed,
        packageVersions: MinimalPackageDetails[]
    ): Promise<void>;

    promotePackageVersions(
        projectId: string,
        feed: Feed,
        feedView: FeedView,
        pkg: Package,
        versions: PackageVersion[]
    ): Promise<void>;
    promotePackageVersions(
        projectId: string,
        feed: Feed,
        feedView: FeedView,
        packageVersions: MinimalPackageDetails[]
    ): Promise<void>;

    getCopyInstallCommand(feed: Feed, pkgName: string, pkgVersion: PackageVersion, separator?: string): string;

    getUpstreamingBehavior(feed: Feed, pkg: Package): Promise<UpstreamingBehavior>;

    setUpstreamingBehavior(feed: Feed, pkg: Package, behavior: UpstreamingBehavior): Promise<void>;
}

export interface IPackageProtocolClient {
    name: string;
    vssIconProps?: IVssIconProps;

    getConnectPanel(feed: Feed, feedViews: FeedView[], hubAction: HubAction): React.ReactNode;
}

export function isPackage(obj: Package | MinimalPackageDetails[]): obj is Package {
    return !Array.isArray(obj);
}
